import { Component, ErrorInfo } from 'react'
import i18next from 'i18next'
import type { ErrorBoundaryProps, ErrorBoundaryState } from './ErrorBoundary.types'
import './ErrorBoundary.scss'

/**
 * Generic Error Boundary with two display variants.
 *
 * @example
 * // Full-page fallback (wraps entire app or top-level routes)
 * <ErrorBoundary variant="page" name="App">
 *   <App />
 * </ErrorBoundary>
 *
 * @example
 * // Inline fallback (wraps a section within a page)
 * <ErrorBoundary variant="inline" name="Routes">
 *   <Routes>...</Routes>
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const name = this.props.name || 'Unknown'
    console.error(`[ErrorBoundary:${name}]`, error, info.componentStack)
  }

  private handleReset = () => {
    this.setState({ hasError: false })
  }

  private handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    const variant = this.props.variant || 'page'

    if (variant === 'page') {
      return (
        <div className="error-boundary error-boundary--page">
          <h1 className="error-boundary__title">
            {i18next.t('errorBoundary.pageTitle')}
          </h1>
          <p className="error-boundary__message">
            {i18next.t('errorBoundary.pageMessage')}
          </p>
          <button className="error-boundary__button" onClick={this.handleReload}>
            {i18next.t('errorBoundary.reload')}
          </button>
        </div>
      )
    }

    return (
      <div className="error-boundary error-boundary--inline">
        <p className="error-boundary__message">
          {i18next.t('errorBoundary.inlineMessage')}
        </p>
        <button className="error-boundary__button" onClick={this.handleReset}>
          {i18next.t('errorBoundary.tryAgain')}
        </button>
      </div>
    )
  }
}
